import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { sendChatMessage, saveHistory } from "../services/api";

const CHAT_UI_STATE = "chat_ui_state";
const STORAGE_KEY = "chat_session";
const TIMEOUT_MINUTES = 30;
const MOBILE_BREAKPOINT = 480;

const getInitialMessage = () => ({
  role: "assistant",
  content: `Hi! 👋 I'm Doug, I am an experimental AI here to answer questions about property taxes in Douglas County. My responses are based on information available to the public on the Douglas County website. The more you use me the better I will get!
`,
});

const initialFirstChat = () => {
  // const storedSession = localStorage.getItem(STORAGE_KEY);
  // if (storedSession) {
  //   const session = JSON.parse(storedSession);
  //   const timeDiff = (Date.now() - session.lastInteraction) / 1000 / 60;
  //   return timeDiff >= TIMEOUT_MINUTES || session.messages.length <= 1;
  // }
  return true;
};

// Function to replace placeholder URLs with actual URLs from sourceMap
const replaceUrlsInContent = (content, sourceMap) => {
  if (!sourceMap || Object.keys(sourceMap).length === 0) {
    return content;
  }
  
  const regex = /<sup>\[\[(.*?)\]\((.*?)\)\]<\/sup>/g;
  let processedContent = content;
  
  processedContent = processedContent.replace(regex, (match, text, url) => {
    // Replace placeholder URLs with actual URLs from sourceMap
    const actualUrl = sourceMap[url] || url;
    return `<sup>[[${text}](${actualUrl})]</sup>`;
  });
  
  return processedContent;
};

const useChatWidget = () => {
  const chatBodyRef = useRef(null);
  const [firstChat, setFirstChat] = useState(initialFirstChat());
  const [conversationId, setConversationId] = useState(uuidv4());
  const [messages, setMessages] = useState([getInitialMessage()]);
  const [userInput, setUserInput] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const [activeSuggestedQuestions, setActiveSuggestedQuestions] =useState(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef(null);
  const previousMessageCountRef = useRef(0);
  const previousLastMessageContentRef = useRef('');
  const [commentModalState, setCommentModalState] = useState({
    isOpen: false,
    messageIndex: null,
    initialComment: ''
  });

  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const inactivityTimerRef = useRef(null);
  
  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    if (isSessionExpired) return;
    
    const hasUserMessages = messages.some(msg => msg.role === "user");
    if (!hasUserMessages) return;
    
    inactivityTimerRef.current = setTimeout(() => {
      setIsSessionExpired(true);
    }, 5 * 60 * 1000);
  };
  
  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    const hasUserMessages = messages.some(msg => msg.role === "user");
    if (hasUserMessages && !isSessionExpired) {
      resetInactivityTimer();
    }
  }, [messages, isSessionExpired]);
  
  useEffect(() => {
    if (!isSessionExpired) {
      resetInactivityTimer();
    }
  }, [isSessionExpired]);

  const handleExpand = () => {
    resetInactivityTimer();
    setIsExpanded(!isExpanded);
  };

  const handleCommentFeedback = async (messageIndex, comment) => {
    resetInactivityTimer();
    setMessages(prevMessages => {
      const updatedMessages = prevMessages.map((msg, idx) => 
        idx === messageIndex ? { ...msg, feedback_comment: comment } : msg
      );
      
      saveHistory(updatedMessages, conversationId).catch(error => 
        console.error('Error saving feedback comment:', error)
      );
      
      return updatedMessages;
    });
  };

  const openCommentModal = (messageIndex) => {
    resetInactivityTimer();
    const currentComment = messages[messageIndex]?.feedback_comment || '';
    setCommentModalState({
      isOpen: true,
      messageIndex,
      initialComment: currentComment
    });
  };
  
  const closeCommentModal = () => {
    resetInactivityTimer();
    setCommentModalState({
      isOpen: false,
      messageIndex: null,
      initialComment: ''
    });
  };

  // useEffect(() => {
  //   const storedSession = localStorage.getItem(STORAGE_KEY);
  //   if (storedSession) {
  //     const session = JSON.parse(storedSession);
  //     const timeDiff = (Date.now() - session.lastInteraction) / 1000 / 60;
  //     if (timeDiff < TIMEOUT_MINUTES) {
  //       setMessages(session.messages);
  //       setConversationId(session.conversationId);
  //       setFirstChat(session.messages.length <= 1);
  //     } else {
  //       localStorage.removeItem(STORAGE_KEY);
  //     }
  //   }
  // }, []);

  // Removed: No longer persisting isOpen state to localStorage
  // Widget now always defaults to closed on page load/refresh

  // useEffect(() => {
  //   if (messages.length > 1) {
  //     const sessionData = {
  //       conversationId,
  //       messages,
  //       lastInteraction: Date.now(),
  //     };
  //     localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
  //   }
  // }, [messages, conversationId]);

  useEffect(() => {
    if (messages.length > 1) {
      setFirstChat(false);
    }
    
    const buttons = messages[messages.length - 1]?.buttons;
    setActiveSuggestedQuestions(buttons);
  
    const lastMessageContent = messages.length > 0 ? messages[messages.length - 1].content : '';
    
    const messageCountChanged = messages.length !== previousMessageCountRef.current;
    const contentChanged = lastMessageContent !== previousLastMessageContentRef.current;
    
    if (messageCountChanged || contentChanged) {
      scrollToBottomChat();
    }
    
    previousMessageCountRef.current = messages.length;
    previousLastMessageContentRef.current = lastMessageContent;
  }, [messages]);

  const handleFeedback = async (messageIndex, feedbackType) => {
    resetInactivityTimer();
    setMessages(prevMessages => {
      const updatedMessages = prevMessages.map((msg, idx) => 
        idx === messageIndex ? { ...msg, feedback_type: feedbackType } : msg
      );
      
      saveHistory(updatedMessages, conversationId).catch(error => console.error('Error saving feedback history:', error));
      
      return updatedMessages;
    });
  };

  const handleReset = () => {
    // const newSession = {
    //   messages: [getInitialMessage()],
    //   conversationId: uuidv4(),
    //   lastInteraction: Date.now(),
    // };

    setMessages([getInitialMessage()]);
    setConversationId(uuidv4());
    setFirstChat(true);
    setUserInput("");
    setIsSessionExpired(false);
    setIsExpanded(false);
    // Keep widget open, just collapse it back to small state

    // localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
  };

  const handleSendMessage = async (text) => {
    resetInactivityTimer();
    if (userInput.trim() || text) {
      setLoading(true);
      const cleanInput = (input) =>
        input
          .replace(/\u00A0/g, " ")
          .replace(/\s+/g, " ")
          .trim();
  
      const userMessageContent = cleanInput(userInput || text);
      const newUserMessage = { role: "user", content: userMessageContent };
  
      const isFirstUserMessage =
        messages.length === 1 && messages[0].role === "assistant";
  
      if (isFirstUserMessage) {
        setIsExpanded(true);
      }
  
      setMessages((prevMessages) => [
        ...prevMessages,
        newUserMessage,
        { role: "assistant", content: "" },
      ]);
  
      setUserInput("");
  
      // Add retry logic
      const attemptSendMessage = async (retryCount = 0) => {
        let receivedContent = false; // Track if we received any actual content
  
        try {
          await sendChatMessage(
            [...messages, newUserMessage],
            conversationId,
            (chunk, isEndOfStream, sourceMap) => {

              if (!isEndOfStream && chunk) {
                receivedContent = true; // We got some actual content
              }
  
              setMessages((prevMessages) => {
                const lastIndex = prevMessages.length - 1;
                const lastMessage = prevMessages[lastIndex];
  
                if (!isEndOfStream) {
                  let updatedContent = lastMessage.content + chunk;
                  
                  // If we have a sourceMap, immediately replace URLs in the current content
                  if (sourceMap && Object.keys(sourceMap).length > 0) {
                    updatedContent = replaceUrlsInContent(updatedContent, sourceMap);
                  }
                  
                  return [
                    ...prevMessages.slice(0, lastIndex),
                    {
                      ...lastMessage,
                      content: updatedContent,
                    },
                  ];
                } else {
                  // Check if we received empty response
                  if (!receivedContent && retryCount < 2) {
                    // Retry the request (max 2 retries)
                    console.log(`Empty response received, retrying... (attempt ${retryCount + 2})`);
                    setTimeout(() => attemptSendMessage(retryCount + 1), 500);
                    return prevMessages; // Don't update messages yet
                  }
  
                  // Normal processing for non-empty responses
                  let parsedQuestions = null;
                  let finalContent = lastMessage.content;
  
                  // Replace placeholder URLs with actual URLs from sourceMap
                  if (sourceMap && Object.keys(sourceMap).length > 0) {
                    finalContent = replaceUrlsInContent(finalContent, sourceMap);
                  }

                  const jsonPattern = /\{[^{}]*"q1"[^{}]*"q2"[^{}]*\}/;
                  try {
                    const jsonMatch = finalContent.match(jsonPattern);
  
                    if (jsonMatch) {
                      parsedQuestions = JSON.parse(jsonMatch[0]);
                      finalContent = finalContent
                        .replace(jsonMatch[0], "")
                        .trim();
                    }
                  } catch (error) {
                    console.error("Error parsing JSON:", error);
                  }
  
                  return [
                    ...prevMessages.slice(0, lastIndex),
                    {
                      ...lastMessage,
                      content: finalContent,
                      ...(parsedQuestions && {
                        buttons: [
                          ...(parsedQuestions.q1
                            ? [
                                {
                                  type: "button",
                                  text: parsedQuestions.q1,
                                  action: () =>
                                    handleOptionSelection(parsedQuestions.q1),
                                },
                              ]
                            : []),
                          ...(parsedQuestions.q2
                            ? [
                                {
                                  type: "button",
                                  text: parsedQuestions.q2,
                                  action: () =>
                                    handleOptionSelection(parsedQuestions.q2),
                                },
                              ]
                            : []),
                        ],
                      }),
                    },
                  ];
                }
              });
              scrollToBottomChat();
            }
          );
  
          // Only set loading to false if we got content or exhausted retries
          if (receivedContent || retryCount >= 2) {
            setLoading(false);
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }, 100);
            
            setMessages(currentMessages => {
              saveHistory(currentMessages, conversationId).catch(error => 
                console.error('Error saving history:', error)
              );
              return currentMessages;
            });
          }
        } catch (error) {
          setMessages((prevMessages) => [
            ...prevMessages.slice(0, -1),
            {
              role: "assistant",
              content:
                "I apologize, but I encountered an error processing your request. Please try again.",
            },
          ]);
          setLoading(false);
        }
      };
  
      // Start the first attempt
      await attemptSendMessage();
    }
  };

  const handleToggle = () => {
    resetInactivityTimer();
    setIsOpen(!isOpen);
  };

  const handleOptionSelection = (text) => {
    resetInactivityTimer();
    setUserInput(text);
    handleSendMessage(text);
  };

  const handleClose = () => {
    resetInactivityTimer();
    setIsOpen(false);
    setIsExpanded(false);
  };

  const scrollToBottomChat = () => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleCopy = (content, index) => {
    resetInactivityTimer();
    navigator.clipboard.writeText(content)
      .then(() => {
        setCopiedMessageIndex(index);
        setTimeout(() => {
          setCopiedMessageIndex(null);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  return {
    isOpen,
    isLoading,
    firstChat,
    messages,
    userInput,
    chatBodyRef,
    isExpanded,
    inputRef,
    setUserInput,
    handleToggle,
    handleSendMessage,
    handleOptionSelection,
    handleClose,
    handleReset,
    handleExpand,
    handleFeedback,
    handleCopy,
    activeSuggestedQuestions,
    copiedMessageIndex,
    handleCommentFeedback,
    commentModalState,
    openCommentModal,
    closeCommentModal,
    isSessionExpired,
    resetInactivityTimer
  };
};

export default useChatWidget;